import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api";

type BackupFile = {
  name: string;
  size?: number;
  modified_utc?: string;
  mtime?: string;
  created_at?: string;
  path?: string;
};

type BackupsResponse =
  | {
      backup_dir?: string;
      count?: number;
      files?: BackupFile[];
      items?: BackupFile[];
      results?: BackupFile[];
    }
  | BackupFile[];

function clean(v: any) {
  return String(v ?? "").trim();
}

function normalizeFile(x: any): BackupFile {
  return {
    name: clean(x?.name || x?.filename || x?.file_name || x?.basename),
    size: Number.isFinite(Number(x?.size)) ? Number(x?.size) : undefined,
    modified_utc: clean(x?.modified_utc || x?.modified || x?.updated_at),
    mtime: clean(x?.mtime),
    created_at: clean(x?.created_at),
    path: clean(x?.path),
  };
}

function asArray(resp: any): BackupFile[] {
  const arr =
    (Array.isArray(resp) && resp) ||
    (resp && Array.isArray(resp.files) && resp.files) ||
    (resp && Array.isArray(resp.items) && resp.items) ||
    (resp && Array.isArray(resp.results) && resp.results) ||
    [];

  return (arr as any[]).map(normalizeFile).filter((x) => !!x.name);
}

function bytes(n?: number) {
  if (!n && n !== 0) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let x = n;
  let i = 0;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i += 1;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

async function getWithSlashFallback(url: string, config?: any) {
  try {
    return await api.get(url, config);
  } catch (err: any) {
    const status = err?.response?.status;
    const shouldRetry = !err?.response || status === 307 || status === 308 || status === 404;
    if (shouldRetry && !url.endsWith("/")) return await api.get(url + "/", config);
    throw err;
  }
}

async function postWithSlashFallback(url: string, data?: any, config?: any) {
  try {
    return await api.post(url, data, config);
  } catch (err: any) {
    const status = err?.response?.status;
    const shouldRetry = !err?.response || status === 307 || status === 308 || status === 404;
    if (shouldRetry && !url.endsWith("/")) return await api.post(url + "/", data, config);
    throw err;
  }
}

async function getTry(urls: string[], config?: any) {
  let lastErr: any = null;
  for (const u of urls) {
    try {
      return await getWithSlashFallback(u, config);
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function postTry(urls: string[], data?: any, config?: any) {
  let lastErr: any = null;
  for (const u of urls) {
    try {
      return await postWithSlashFallback(u, data, config);
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function downloadBlobTry(urls: string[], filename: string) {
  const res = await getTry(urls, { responseType: "blob" });
  const blob = new Blob([res.data]);
  const objectUrl = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(objectUrl);
}

export default function AdminBackups() {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<null | string>(null);
  const [error, setError] = useState<string>("");
  const [info, setInfo] = useState<BackupsResponse | null>(null);

  const files = useMemo(() => asArray(info), [info]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const r = await getTry([
        "/admin/backups",
        "/admin/backup/list",
        "/admin/backups/list",
        "/admin/backup",
      ]);

      setInfo(r.data);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 404) {
        setError("Backup endpoints are not enabled yet. Frontend is installed correctly.");
      } else {
        setError(e?.response?.data?.detail || e?.message || "Failed to load backups");
      }
      setInfo(null);
      console.error("[ADMIN BACKUPS LOAD]", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const doDbBackup = useCallback(async () => {
    setBusy("db-backup");
    setError("");

    try {
      await postTry(
        [
          "/admin/backup/db",
          "/admin/backups/db",
          "/admin/backup/create-db",
        ],
        {}
      );

      const stamp = new Date().toISOString().replace(/[:.]/g, "-");

      await downloadBlobTry(
        [
          "/admin/backup/db/download",
          "/admin/backups/db/download",
          "/admin/backup/download/db",
        ],
        `doctor_is_in_db_backup_${stamp}.zip`
      );

      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "DB backup failed");
      console.error("[ADMIN BACKUPS DB]", e);
    } finally {
      setBusy(null);
    }
  }, [refresh]);

  const doFullDownload = useCallback(async (includeUploads: boolean) => {
    setBusy(includeUploads ? "full-with-uploads" : "full-no-uploads");
    setError("");

    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const q = includeUploads ? "true" : "false";

      await downloadBlobTry(
        [
          `/admin/backup/full/download?include_uploads=${q}`,
          `/admin/backups/full/download?include_uploads=${q}`,
          `/admin/backup/download/full?include_uploads=${q}`,
        ],
        `doctor_is_in_full_backup_${includeUploads ? "with_uploads" : "no_uploads"}_${stamp}.zip`
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Full backup download failed");
      console.error("[ADMIN BACKUPS FULL]", e);
    } finally {
      setBusy(null);
    }
  }, []);

  const downloadNamedFile = useCallback(async (file: BackupFile) => {
    const name = clean(file.name);
    if (!name) return;

    setBusy(`file:${name}`);
    setError("");

    try {
      await downloadBlobTry(
        [
          `/admin/backups/download/${encodeURIComponent(name)}`,
          `/admin/backup/download/${encodeURIComponent(name)}`,
          `/admin/backups/file/${encodeURIComponent(name)}`,
          `/admin/backup/file/${encodeURIComponent(name)}`,
        ],
        name
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || `Failed to download ${name}`);
      console.error("[ADMIN BACKUPS ITEM DOWNLOAD]", e);
    } finally {
      setBusy(null);
    }
  }, []);

  return (
    <div style={{ padding: 18 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Backups</h2>
          <div style={{ opacity: 0.75, fontSize: 13 }}>
            View and download backup artifacts (DB + full).
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={refresh} disabled={loading || !!busy}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>

          <button onClick={doDbBackup} disabled={loading || !!busy}>
            {busy === "db-backup" ? "Working..." : "Create + Download DB Backup"}
          </button>

          <button onClick={() => doFullDownload(false)} disabled={loading || !!busy}>
            {busy === "full-no-uploads" ? "Working..." : "Download Full (No Uploads)"}
          </button>

          <button onClick={() => doFullDownload(true)} disabled={loading || !!busy}>
            {busy === "full-with-uploads" ? "Working..." : "Download Full (+ Uploads)"}
          </button>
        </div>
      </div>

      {error ? (
        <div
          style={{
            background: "#ffecec",
            border: "1px solid #ffb5b5",
            padding: 10,
            borderRadius: 10,
            marginBottom: 12,
          }}
        >
          <b>Error:</b> {error}
        </div>
      ) : null}

      <div style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 12, overflow: "hidden" }}>
        <div
          style={{
            padding: 10,
            background: "rgba(0,0,0,0.04)",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 700 }}>Files</div>
          <div style={{ opacity: 0.75 }}>{files.length} item(s)</div>
        </div>

        <div style={{ padding: 10 }}>
          {files.length === 0 ? (
            <div style={{ opacity: 0.75 }}>No backups returned.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {files.map((f, idx) => {
                const stamp = clean(f.modified_utc || f.mtime || f.created_at);
                const isDownloading = busy === `file:${f.name}`;

                return (
                  <div
                    key={`${f.name}-${idx}`}
                    style={{
                      border: "1px solid rgba(0,0,0,0.10)",
                      borderRadius: 10,
                      padding: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: "1 1 420px" }}>
                      <div
                        style={{
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {f.name}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.75 }}>
                        {bytes(f.size)} {stamp ? `• ${stamp}` : ""}
                      </div>
                    </div>

                    <div>
                      <button onClick={() => downloadNamedFile(f)} disabled={loading || !!busy}>
                        {isDownloading ? "Downloading..." : "Download"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}